import { z } from "zod";
import { workerIdParamSchema } from "./certifications.js";

export const workerDocumentTypeSchema = z.enum([
  "DRIVER_LICENSE",
  "APPLICATION",
  "I9",
  "W4",
  "CERTIFICATION",
  "SAFETY_CARD",
  "EQUIPMENT_LICENSE",
  "OTHER",
]);

export const workerDocumentSensitivitySchema = z.enum([
  "FIELD",
  "HR",
  "TAX",
  "IDENTITY",
  "CONFIDENTIAL",
]);

export const workerDocumentStorageStatusSchema = z.enum(["ACTIVE", "EXPIRED", "DELETED"]);

export const workerDocumentAccessLevelSchema = z.enum([
  "ADMIN_ONLY",
  "PAYROLL_ONLY",
  "FIELD_ALLOWED",
]);

export const workerDocumentRetentionRuleSchema = z.enum([
  "I9_US_TEMPLATE",
  "W4_US_TEMPLATE",
  "FIELD_CERTIFICATION_EXPIRATION",
  "DRIVER_LICENSE_EXPIRATION",
  "CUSTOM",
]);

export const workerDocumentScanStatusSchema = z.enum([
  "PENDING_SCAN",
  "CLEAN",
  "QUARANTINED",
  "SCAN_FAILED",
]);

export const workerDocumentDisplayStatusSchema = z.enum([
  "VALID",
  "EXPIRING_SOON",
  "EXPIRED",
  "MISSING",
]);

export const WORKER_DOCUMENT_TYPE_LABELS: Record<WorkerDocumentType, string> = {
  DRIVER_LICENSE: "Driver License",
  APPLICATION: "Application",
  I9: "I-9",
  W4: "W-4",
  CERTIFICATION: "Certification",
  SAFETY_CARD: "Safety Card",
  EQUIPMENT_LICENSE: "Equipment License",
  OTHER: "Other",
};

export const workerDocumentSchema = z.object({
  id: z.string().uuid(),
  workerId: z.string().uuid(),
  companyId: z.string().uuid(),
  documentType: workerDocumentTypeSchema,
  fileName: z.string(),
  contentType: z.string(),
  fileSize: z.number().int().nonnegative(),
  sha256Hash: z.string(),
  sensitivity: workerDocumentSensitivitySchema,
  accessLevel: workerDocumentAccessLevelSchema,
  status: workerDocumentStorageStatusSchema,
  displayStatus: workerDocumentDisplayStatusSchema,
  uploadedById: z.string().uuid().nullable(),
  uploadedByName: z.string().nullable(),
  expiresAt: z.string().nullable(),
  retentionRule: workerDocumentRetentionRuleSchema,
  retentionUntil: z.string().nullable(),
  legalHold: z.boolean(),
  purgeAfter: z.string().nullable(),
  scanStatus: workerDocumentScanStatusSchema,
  notes: z.string().nullable(),
  deletedAt: z.string().nullable(),
  deletionReason: z.string().nullable(),
  isExpired: z.boolean(),
  expiresSoon: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const workerDocumentListSchema = z.object({
  items: z.array(workerDocumentSchema),
  canUpload: z.boolean(),
  canViewAllTypes: z.boolean(),
  requireDownloadReasonForSensitiveDocs: z.boolean(),
  compliance: z.object({
    fieldDocumentsComplete: z.boolean(),
    hrDocumentsComplete: z.boolean(),
    expiringSoon: z.number().int(),
    missingRequiredDocuments: z.array(workerDocumentTypeSchema),
  }),
});

export const workerDocumentIdParamSchema = workerIdParamSchema.extend({
  documentId: z.string().uuid(),
});

export const updateWorkerDocumentMetadataSchema = z
  .object({
    expiresAt: z.string().date().nullable().optional(),
    retentionRule: workerDocumentRetentionRuleSchema.optional(),
    retentionUntil: z.string().date().nullable().optional(),
    legalHold: z.boolean().optional(),
    purgeAfter: z.string().datetime().nullable().optional(),
    sensitivity: workerDocumentSensitivitySchema.optional(),
    accessLevel: workerDocumentAccessLevelSchema.optional(),
    notes: z.string().trim().max(5000).nullable().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, "At least one field is required");

export const deleteWorkerDocumentSchema = z.object({
  deletionReason: z.string().trim().min(1).max(1000),
});

export const purgeWorkerDocumentSchema = z.object({
  confirmation: z.literal("PURGE"),
  reason: z.string().trim().min(1).max(1000),
});

export const updateWorkerDocumentScanStatusSchema = z.object({
  scanStatus: workerDocumentScanStatusSchema,
  reason: z.string().trim().min(1).max(1000),
});

export type WorkerDocumentType = z.infer<typeof workerDocumentTypeSchema>;
export type WorkerDocumentSensitivity = z.infer<typeof workerDocumentSensitivitySchema>;
export type WorkerDocumentStorageStatus = z.infer<typeof workerDocumentStorageStatusSchema>;
export type WorkerDocumentAccessLevel = z.infer<typeof workerDocumentAccessLevelSchema>;
export type WorkerDocumentRetentionRule = z.infer<typeof workerDocumentRetentionRuleSchema>;
export type WorkerDocumentScanStatus = z.infer<typeof workerDocumentScanStatusSchema>;
export type WorkerDocumentDisplayStatus = z.infer<typeof workerDocumentDisplayStatusSchema>;
export type WorkerDocument = z.infer<typeof workerDocumentSchema>;
export type WorkerDocumentList = z.infer<typeof workerDocumentListSchema>;
